import _ from 'lodash';

/** 菜品默认图（大图）在 bundled 与上传 query 中的语义文件名 */
export const BUNDLED_DISH_DEFAULT_IMAGE_FILE = 'menu_default.jpg';
/** 菜品默认图（列表/小图）文件名 */
export const BUNDLED_DISH_DEFAULT_SMALL_FILE = 'menu_default_s.jpg';

/**
 * 活动公告图槽位总数，与 v1 上传名 `activity-bulletin{N}.jpg` 及 Figma 位图数量一致。
 * 后端 `activity-bulletin` 可能按任意顺序返回，解析时按 URL 中的 N 归位到第 N 张。
 */
export const ACTIVITY_BULLETIN_MAX_SLOTS = 8;

/**
 * 从活动公告图 URL 中解析槽位序号（与 `activity-bulletin{slotIndex}.jpg` 约定一致）。
 * @param url - 完整 URL
 * @returns 1..ACTIVITY_BULLETIN_MAX_SLOTS，无法识别时返回 null
 */
function parseActivityBulletinSlotFromUrl(url: string): number | null {
    const s = String(url || '').trim();
    if (!s) {
        return null;
    }
    const m = s.match(/activity-bulletin(\d+)\.jpe?g/i);
    if (!m) {
        return null;
    }
    const n = parseInt(m[1], 10);
    if (!Number.isFinite(n) || n < 1 || n > ACTIVITY_BULLETIN_MAX_SLOTS) {
        return null;
    }
    return n;
}

/**
 * 将接口返回的任意顺序活动公告 URL 列表，归一为固定长度、按下标 0 对应第 1 张的数组。
 * @param urls - 原始 URL 列表
 */
function normalizeActivityBulletinUrlsBySlot(urls: string[]): string[] {
    const out = new Array(ACTIVITY_BULLETIN_MAX_SLOTS).fill('') as string[];
    for (const u of urls) {
        const slot = parseActivityBulletinSlotFromUrl(u);
        if (slot !== null) {
            out[slot - 1] = u.trim();
        }
    }
    return out;
}

/**
 * 图片就餐指南槽位总数，与 v1 上传 query `number` 1..N 及 `bulletin{N}.jpg` 一致（与活动公告 `activity-bulletin{N}.jpg` 区分）。
 * 后端 `bulletin` 列表可能乱序，解析时按 URL 中的 N 归位到第 N 张。
 */
export const BULLETIN_DINING_GUIDE_MAX_SLOTS = 5;

/**
 * MOP 首屏轮播槽位数量，与 v1 `bulletin-mop-carousel-image/{index}` 的 `index` 仅 `0`–`4` 及文件名 `{index}_<uuid>.jpg` 一致。
 * 后端 `mop-carousel` 可能为稀疏或乱序数组，解析时按 **basename 前缀** 归位到对应槽位（与活动公告等页一致）。
 */
export const MOP_CAROUSEL_MAX_SLOTS = 5;

/**
 * 从 MOP 轮播图 URL 中解析槽位下标（与 OSS 保存名 `{index}_....jpg` 一致，`index` 仅 0–4）。
 * @param url - 完整 URL 或含路径的字符串
 * @returns 0..4，无法识别时返回 `null`
 */
function parseMopCarouselSlotFromUrl(url: string): number | null {
    const s = String(url || '').trim();
    if (!s) {
        return null;
    }
    let pathPart = s.split('?')[0];
    try {
        if (/^https?:\/\//i.test(pathPart)) {
            pathPart = new URL(pathPart).pathname;
        }
    } catch {
        return null;
    }
    const base = pathPart.split('/').filter(Boolean).pop() || '';
    const m = base.match(/^([0-4])_[a-zA-Z0-9._-]+\.(jpe?g|png|webp)$/i);
    if (!m) {
        return null;
    }
    return parseInt(m[1], 10);
}

/**
 * 将接口返回的任意顺序 MOP 轮播 URL 列表，归一为固定长度 5、下标与槽位 `0`–`4` 一致的数组。
 * @param urls - 原始 URL 列表
 */
function normalizeMopCarouselUrlsBySlot(urls: string[]): string[] {
    const out = new Array(MOP_CAROUSEL_MAX_SLOTS).fill('') as string[];
    for (const u of urls) {
        const slot = parseMopCarouselSlotFromUrl(u);
        if (slot !== null) {
            out[slot] = u.trim();
        }
    }
    return out;
}

/**
 * 从图片就餐指南 URL 中解析槽位序号（仅匹配 `bulletin{n}.jpg`，不匹配 `activity-bulletin{n}.jpg`）。
 * @param url - 完整 URL
 * @returns 1..BULLETIN_DINING_GUIDE_MAX_SLOTS，无法识别时返回 null
 */
function parseBulletinDiningGuideSlotFromUrl(url: string): number | null {
    const s = String(url || '').trim();
    if (!s) {
        return null;
    }
    const m = s.match(/(?<!activity-)bulletin(\d+)\.jpe?g/i);
    if (!m) {
        return null;
    }
    const n = parseInt(m[1], 10);
    if (!Number.isFinite(n) || n < 1 || n > BULLETIN_DINING_GUIDE_MAX_SLOTS) {
        return null;
    }
    return n;
}

/**
 * 将接口返回的任意顺序「图片就餐指南」URL 归一为固定长度、下标 0 对应第 1 张的数组。
 * @param urls - 原始 URL 列表
 */
function normalizeBulletinDiningGuideUrlsBySlot(urls: string[]): string[] {
    const out = new Array(BULLETIN_DINING_GUIDE_MAX_SLOTS).fill('') as string[];
    for (const u of urls) {
        const slot = parseBulletinDiningGuideSlotFromUrl(u);
        if (slot !== null) {
            out[slot - 1] = u.trim();
        }
    }
    return out;
}

/**
 * 从 `GET .../restaurant-static/bundled-image-urls` 解析后的视图模型（字段与 OpenAPI 示例对齐，并做宽松兼容）。
 */
export type RestaurantStaticBundledView = {
    /** 餐厅环境图完整 URL（可能为空字符串） */
    backgroundUrl: string;
    /** QOP 餐厅 Logo 完整 URL（后端字段 `qopLogo`） */
    qopLogoUrl: string;
    /** 活动公告图 URL，长度固定为 `ACTIVITY_BULLETIN_MAX_SLOTS`，下标 0 为第 1 张，由路径中 `activity-bulletin{N}.jpg` 归位 */
    activityBulletinUrls: string[];
    /** 图片就餐指南 URL，长度固定为 `BULLETIN_DINING_GUIDE_MAX_SLOTS`，下标 0 为第 1 张，由路径中 `bulletin{N}.jpg` 归位 */
    bulletinUrls: string[];
    /** MOP 首屏轮播图 URL，长度固定为 `MOP_CAROUSEL_MAX_SLOTS`，下标与槽位 0–4 一致，由路径中 `N_...` 文件名归位 */
    mopCarouselUrls: string[];
    /** QOP 自定义模板相关条目（字符串多为完整 URL 或文件名，由展示侧再判断） */
    qopMouldEntries: string[];
    /** 菜品默认图：大图、小图 URL */
    dishDefault: { imageUrl: string; smallUrl: string };
    /**
     * 推荐菜/单品图预览：key 为 `menu_item_id` 字符串；来自 bundled 扩展字段（若后端未返回则空对象）。
     */
    dishPreviewByMenuItemId: Record<string, { imageUrl?: string; smallUrl?: string }>;
    /** 原始响应，便于后续扩展字段 */
    raw: unknown;
};

/**
 * 将接口可能返回的「字符串 / { url }」统一为 URL 字符串列表。
 * @param v - 任意 JSON 片段
 * @returns 非空 URL 列表
 */
function coerceUrlList(v: unknown): string[] {
    if (!Array.isArray(v)) {
        return [];
    }
    const out: string[] = [];
    for (const item of v) {
        if (typeof item === 'string') {
            const s = item.trim();
            if (s) {
                out.push(s);
            }
            continue;
        }
        if (item && typeof item === 'object') {
            const u = _.get(item, 'url') ?? _.get(item, 'image_url') ?? _.get(item, 'image');
            if (typeof u === 'string' && u.trim()) {
                out.push(u.trim());
            }
        }
    }
    return out;
}

/**
 * 尝试从 bundled 扩展字段中解析「按 menu_item_id 索引的菜品图 URL」。
 * @param data - 已取 `data` 或根对象的平铺字段
 */
function parseDishPreviewMap(data: Record<string, unknown>): Record<string, { imageUrl?: string; smallUrl?: string }> {
    const candidates = [
        _.get(data, 'dish-images'),
        _.get(data, 'dishImages'),
        _.get(data, 'dish_images'),
        _.get(data, 'recommend-dish-images'),
    ];
    const out: Record<string, { imageUrl?: string; smallUrl?: string }> = {};
    for (const c of candidates) {
        if (!c || typeof c !== 'object' || Array.isArray(c)) {
            continue;
        }
        const rec = c as Record<string, unknown>;
        for (const key of Object.keys(rec)) {
            const v = rec[key];
            if (typeof v === 'string') {
                const s = v.trim();
                if (s) {
                    out[key] = { smallUrl: s, imageUrl: s };
                }
                continue;
            }
            if (v && typeof v === 'object') {
                const row = v as Record<string, unknown>;
                const imageUrlRaw = row.image ?? row.imageUrl ?? row.url;
                const smallUrlRaw = row.small ?? row.smallUrl ?? row.thumb;
                out[key] = {
                    imageUrl: typeof imageUrlRaw === 'string' ? imageUrlRaw.trim() : undefined,
                    smallUrl: typeof smallUrlRaw === 'string' ? smallUrlRaw.trim() : undefined,
                };
            }
        }
        if (Object.keys(out).length > 0) {
            break;
        }
    }
    return out;
}

/**
 * 解析 `bundled-image-urls` 的 JSON 体为页面可用的结构化数据。
 * @param raw - axios 返回体（可能包一层 `data`）
 * @returns 结构化视图；解析失败时返回各字段默认空值
 */
export function parseRestaurantStaticBundled(raw: unknown): RestaurantStaticBundledView {
    const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const data = (root.data !== undefined && typeof root.data === 'object' ? root.data : root) as Record<string, unknown>;

    const dishDefaultObj = (_.get(data, 'dish-default') ?? _.get(data, 'dishDefault')) as Record<string, unknown> | undefined;
    const imageUrl = String(_.get(dishDefaultObj, 'image') ?? '').trim();
    const smallUrl = String(_.get(dishDefaultObj, 'small') ?? '').trim();

    return {
        backgroundUrl: String(data.background ?? '').trim(),
        qopLogoUrl: typeof data.qopLogo === 'string' ? data.qopLogo.trim() : '',
        activityBulletinUrls: normalizeActivityBulletinUrlsBySlot(
            coerceUrlList(data['activity-bulletin'] ?? data.activityBulletin),
        ),
        bulletinUrls: normalizeBulletinDiningGuideUrlsBySlot(coerceUrlList(data.bulletin)),
        mopCarouselUrls: normalizeMopCarouselUrlsBySlot(
            coerceUrlList(data['mop-carousel'] ?? data.mopCarousel),
        ),
        qopMouldEntries: coerceUrlList(data.qopMould ?? data['qop-mould']),
        dishDefault: { imageUrl, smallUrl },
        dishPreviewByMenuItemId: parseDishPreviewMap(data),
        raw,
    };
}

/**
 * 为图片 URL 追加缓存破坏参数（不改变已有 query 的语义，仅追加 `cb`）。
 * @param url - 完整 URL；空串时原样返回
 * @param bust - 版本号或时间戳
 * @returns 带 `cb` 的 URL；空入参返回 `undefined` 便于 AvatarCard 不展示
 */
export function withImageCacheBust(url: string | undefined, bust: number): string | undefined {
    const u = String(url || '').trim();
    if (!u) {
        return undefined;
    }
    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}cb=${encodeURIComponent(String(bust))}`;
}

/**
 * 取推荐菜在 bundled 中的预览地址；若无则返回 `undefined`（由调用方决定是否回退旧静态根路径）。
 * @param view - 解析后的 bundled 视图
 * @param menuItemId - 菜品 `menu_item_id`
 * @param bust - 缓存破坏参数
 */
export function getRecommendDishBundledAvatarUrls(
    view: RestaurantStaticBundledView,
    menuItemId: string | number,
    bust: number,
): { avatarUrl?: string; avatarUrlMax?: string } {
    const id = String(menuItemId ?? '').trim();
    if (!id) {
        return {};
    }
    const row = view.dishPreviewByMenuItemId[id];
    if (!row) {
        return {};
    }
    return {
        avatarUrl: withImageCacheBust(row.smallUrl || row.imageUrl, bust),
        avatarUrlMax: withImageCacheBust(row.imageUrl || row.smallUrl, bust),
    };
}
